function customSlider(element, options = {}) {
  const defaults = {
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: true,
    arrows: true,
    autoplay: false,
    autoplaySpeed: 3000,
    infinite: false,
    touchThreshold: 50,
    gap: 20,
    responsive: []
  };

  const baseSettings = { ...defaults, ...options };
  const slider =
    typeof element === "string"
      ? document.querySelector(element)
      : element;

  if (!slider) return;

  const originalSlides = Array.from(slider.children);
  let slides = [];
  let track, controls;
  let dots = [], dotsWrap, prevArrow, nextArrow;
  let index = 0;
  let isInitialized = false;
  let autoplayTimer;
  let lastSlidesToShow = null;

  /* ================= SETTINGS ================= */
  function getSettings() {
    let current = { ...baseSettings };
    const sorted = [...baseSettings.responsive].sort(
      (a, b) => b.breakpoint - a.breakpoint
    );

    sorted.forEach(bp => {
      if (window.innerWidth <= bp.breakpoint) {
        current = { ...current, ...bp.settings };
      }
    });
    return current;
  }

  function sliderWidth() {
    return slider.clientWidth;
  }

  function actualSlideWidth() {
    const { slidesToShow, gap } = slider._settings;
    const totalGaps = gap * (slidesToShow - 1);
    return (sliderWidth() - totalGaps) / slidesToShow;
  }

  function stepWidth() {
    return actualSlideWidth(); // ❗ gap NOT included
  }

  function maxIndex() {
    return Math.max(0, slides.length - slider._settings.slidesToShow);
  }

  function clampIndex() {
    index = Math.max(0, Math.min(index, maxIndex()));
  }

  /* ================= INIT ================= */
  function init() {
    if (isInitialized) return;

    slider._settings = getSettings();
    if (originalSlides.length <= slider._settings.slidesToShow) return;

    isInitialized = true;
    index = 0;
    lastSlidesToShow = slider._settings.slidesToShow;

    slider.classList.add("custom-slider");
    slider.style.position = "relative";
    slider.style.overflow = "hidden";

    slides = [...originalSlides];

    track = document.createElement("div");
    track.className = "custom-track";
    track.style.display = "flex";
    track.style.gap = `${slider._settings.gap}px`;
    track.style.willChange = "transform";

    slides.forEach(slide => {
      slide.classList.add("custom-slide");
      slide.style.boxSizing = "border-box";
      slide.style.flex = "0 0 auto";
      track.appendChild(slide);
    });

    slider.innerHTML = "";
    slider.appendChild(track);

    controls = document.createElement("div");
    controls.className = "custom-slider-controls";
    slider.appendChild(controls);

    buildControls();
    bindSwipe();

    requestAnimationFrame(() => {
      setWidths();
      update(false);
    });

    startAutoplay();
  }

  /* ================= WIDTHS ================= */
  function setWidths() {
    const w = actualSlideWidth();

    slides.forEach(slide => {
      slide.style.width = `${w}px`;
      slide.style.minWidth = `${w}px`;
      slide.style.maxWidth = `${w}px`;
    });
  }

  /* ================= UPDATE ================= */
  function update(animate = true) {
    clampIndex();
    track.style.transition = animate ? "transform 0.35s ease" : "none";
    track.style.transform = `translateX(-${index * stepWidth()}px)`;
    updateDots();
    updateArrows();
  }

  function next() {
    if (slider._settings.infinite) {
      index = (index + slider._settings.slidesToScroll) % (maxIndex() + 1);
    } else {
      index += slider._settings.slidesToScroll;
    }
    update();
    startAutoplay();
  }

  function prev() {
    if (slider._settings.infinite && index === 0) {
      index = maxIndex();
    } else {
      index -= slider._settings.slidesToScroll;
    }
    update();
    startAutoplay();
  }

  /* ================= CONTROLS ================= */
  function buildControls() {
    controls.innerHTML = "";
    dots = [];
    dotsWrap = prevArrow = nextArrow = null;

    if (slider._settings.arrows) {
      prevArrow = document.createElement("button");
      nextArrow = document.createElement("button");
      prevArrow.className = "custom-arrow prev";
      nextArrow.className = "custom-arrow next";
      prevArrow.innerHTML = "&#10094;";
      nextArrow.innerHTML = "&#10095;";
      prevArrow.onclick = prev;
      nextArrow.onclick = next;
      controls.append(prevArrow, nextArrow);
    }

    if (slider._settings.dots) {
      dotsWrap = document.createElement("div");
      dotsWrap.className = "custom-dots";
      controls.appendChild(dotsWrap);

      const count = Math.ceil(
        (maxIndex() + 1) / slider._settings.slidesToScroll
      );

      for (let i = 0; i < count; i++) {
        const dot = document.createElement("button");
        dot.onclick = () => {
          index = i * slider._settings.slidesToScroll;
          update();
          startAutoplay();
        };
        dots.push(dot);
        dotsWrap.appendChild(dot);
      }
    }
  }

  function updateArrows() {
    if (!prevArrow || !nextArrow) return;
    if (slider._settings.infinite) {
      prevArrow.classList.remove("disabled");
      nextArrow.classList.remove("disabled");
    } else {
      prevArrow.classList.toggle("disabled", index === 0);
      nextArrow.classList.toggle("disabled", index >= maxIndex());
    }
  }

  function updateDots() {
    dots.forEach((dot, i) =>
      dot.classList.toggle(
        "active",
        index === i * slider._settings.slidesToScroll
      )
    );
  }

  /* ================= AUTOPLAY ================= */
  function startAutoplay() {
    if (!slider._settings.autoplay) return;
    stopAutoplay();
    autoplayTimer = setInterval(next, slider._settings.autoplaySpeed);
  }

  function stopAutoplay() {
    clearInterval(autoplayTimer);
  }

  /* ================= SWIPE ================= */
  function bindSwipe() {
    let dragging = false;
    let startX = 0;
    let deltaX = 0;

    track.addEventListener("pointerdown", e => {
      dragging = true;
      startX = e.clientX;
      deltaX = 0;
      track.setPointerCapture(e.pointerId);
      track.style.transition = "none";
      stopAutoplay();
    });

    track.addEventListener("pointermove", e => {
      if (!dragging) return;
      deltaX = e.clientX - startX;
      track.style.transform = `translateX(${
        -(index * stepWidth()) + deltaX
      }px)`;
    });

    track.addEventListener("pointerup", e => {
      dragging = false;
      track.releasePointerCapture(e.pointerId);
      const threshold = slider.clientWidth / slider._settings.touchThreshold;

      if (Math.abs(deltaX) > threshold) {
        deltaX < 0 ? next() : prev();
      } else {
        update();
      }
      startAutoplay();
    });
  }

  /* ================= RESIZE ================= */
  let resizeTimer;
  function check() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const settings = getSettings();
      const changed = lastSlidesToShow !== settings.slidesToShow;
      slider._settings = settings;

      if (originalSlides.length > settings.slidesToShow) {
        if (!isInitialized) {
          init();
        } else {
          if (changed) {
            clampIndex();
            buildControls();
          }
          requestAnimationFrame(() => {
            setWidths();
            update(false);
          });
        }
      }

      lastSlidesToShow = settings.slidesToShow;
    }, 100);
  }

  window.addEventListener("resize", check);

  /* ================= SAFE INIT ================= */
  function waitForWidth(cb, attempts = 0) {
    if (slider.offsetWidth > 0 || attempts > 50) cb();
    else requestAnimationFrame(() => waitForWidth(cb, attempts + 1));
  }

  waitForWidth(check);

  /* ================= PUBLIC API ================= */
  return {
    next,
    prev,
    goTo(i) {
      index = i;
      update();
    },
    destroy() {
      stopAutoplay();
      window.removeEventListener("resize", check);
      slider.innerHTML = "";
      originalSlides.forEach(slide => slider.appendChild(slide));
    }
  };
}
